import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { Maintenance } from '@/hooks/useMaintenances';

interface ExportOptions {
  maintenances: Maintenance[];
  annee: number;
  bateauNom?: string;
}

export const exportMaintenancesPDF = ({ maintenances, annee, bateauNom }: ExportOptions) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Header band
  doc.setFillColor(20, 26, 51); // nautic navy
  doc.rect(0, 0, pageWidth, 28, 'F');

  doc.setTextColor(234, 179, 8); // gold
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Nauticoach', 14, 13);

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`Maintenances effectuées - ${annee}`, 14, 22);

  // Subheader
  doc.setTextColor(60, 60, 60);
  doc.setFontSize(10);
  let y = 36;
  if (bateauNom) {
    doc.text(`Navire : ${bateauNom}`, 14, y);
    y += 5;
  }
  doc.text(`Édité le ${format(new Date(), 'dd/MM/yyyy', { locale: fr })}`, 14, y);
  y += 4;

  // Filter completed maintenances for the year, sorted by date
  const sorted = [...maintenances].sort(
    (a, b) => new Date(a.date_echeance).getTime() - new Date(b.date_echeance).getTime()
  );

  const totalCout = sorted.reduce((sum, m) => sum + (m.prix || 0), 0);

  // Table
  autoTable(doc, {
    startY: y + 4,
    head: [['Date', 'Titre', 'Catégorie', 'Description', 'Coût (€)']],
    body: sorted.map((m) => [
      format(parseISO(m.date_echeance), 'dd/MM/yyyy', { locale: fr }),
      m.titre,
      m.categorie || '-',
      m.description || '-',
      m.prix != null ? m.prix.toLocaleString('fr-FR', { minimumFractionDigits: 2 }) : '-',
    ]),
    foot: [['', '', '', 'Total', totalCout.toLocaleString('fr-FR', { minimumFractionDigits: 2 }) + ' €']],
    headStyles: { fillColor: [20, 26, 51], textColor: [234, 179, 8], fontStyle: 'bold' },
    footStyles: { fillColor: [240, 240, 245], textColor: [20, 26, 51], fontStyle: 'bold' },
    bodyStyles: { fontSize: 9, textColor: [40, 40, 40] },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      0: { cellWidth: 24 },
      1: { cellWidth: 40 },
      2: { cellWidth: 30 },
      3: { cellWidth: 'auto' },
      4: { cellWidth: 25, halign: 'right' },
    },
    margin: { left: 14, right: 14 },
    didDrawPage: () => {
      const pageCount = doc.getNumberOfPages();
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(
        `Nauticoach - Page ${doc.getCurrentPageInfo().pageNumber}/${pageCount}`,
        pageWidth / 2,
        pageHeight - 8,
        { align: 'center' }
      );
    },
  });

  if (sorted.length === 0) {
    const finalY = (doc as any).lastAutoTable?.finalY || y + 10;
    doc.setFontSize(11);
    doc.setTextColor(120, 120, 120);
    doc.text('Aucune maintenance effectuée pour cette année.', 14, finalY + 10);
  }

  doc.save(`maintenances-${annee}${bateauNom ? '-' + bateauNom.replace(/\s+/g, '_') : ''}.pdf`);
};
